import { Dialog } from '@headlessui/react';
import { Copy, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { storeCartItems } from '@/utils/cartStorage';
import { toast } from 'react-hot-toast';
import { formatPrice } from '@/lib/utils';
import { CartItem } from '@/types';
import { calculateCartItemDiscount } from '@/lib/discount';
import { useNavigate } from 'react-router-dom';

interface PaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  paymentMethod: 'bank' | 'paystack' | 'monnify' | 'stripe';
  onPaymentMethodChange: (method: 'bank' | 'paystack' | 'monnify' | 'stripe') => void;
  onConfirm: () => void;
  storeInfo: any;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>, type: 'order' | 'shipping') => void;
  orderTotal: number;
  shippingTotal: number;
  vatAmount: number;
  orderPaymentReceipt: File | null;
  shippingPaymentReceipt: File | null;
  cart?: CartItem[];
  customerEmail?: string;
  customerName?: string;
  isLoading?: boolean;
}

export function PaymentMethodDialog({
  open,
  onClose,
  paymentMethod,
  onPaymentMethodChange,
  onConfirm,
  storeInfo,
  onFileUpload,
  orderTotal,
  shippingTotal,
  vatAmount,
  orderPaymentReceipt,
  shippingPaymentReceipt,
  cart = [],
  customerEmail,
  customerName,
  isLoading = false,
}: PaymentMethodDialogProps) {
  const navigate = useNavigate();
  
  const handleCopyAccountNumber = (accountNumber: string) => {
    navigator.clipboard.writeText(accountNumber);
    toast.success('Account number copied to clipboard');
  };

  const showSeparateShipping = storeInfo?.mb_active === 1 && storeInfo?.sepShipping === 1 && shippingTotal > 0;
  const total = orderTotal + shippingTotal + vatAmount;

  // Calculate discount information
  const cartTotals = cart.reduce((totals, item) => {
    const { totalOriginalPrice, totalDiscountValue, totalFinalPrice } = calculateCartItemDiscount(item);
    return {
      originalSubtotal: totals.originalSubtotal + totalOriginalPrice,
      totalDiscount: totals.totalDiscount + totalDiscountValue,
      finalSubtotal: totals.finalSubtotal + totalFinalPrice
    };
  }, { originalSubtotal: 0, totalDiscount: 0, finalSubtotal: 0 });

  const hasDiscounts = cartTotals.totalDiscount > 0;

  const waitForStripe = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (typeof (window as any).Stripe !== 'undefined') {
        resolve((window as any).Stripe);
        return;
      }

      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      const checkStripe = () => {
        attempts++;
        if (typeof (window as any).Stripe !== 'undefined') {
          resolve((window as any).Stripe);
        } else if (attempts >= maxAttempts) {
          reject(new Error('Stripe.js failed to load. Please refresh the page and try again.'));
        } else {
          setTimeout(checkStripe, 100);
        }
      };
      checkStripe();
    });
  };

  const handleStripePayment = async () => {
    const loadingToast = toast.loading('Processing payment...');
    try {
      if (!storeInfo?.StripeKey) {
        throw new Error('Stripe is not properly configured for this store. Please contact the store administrator.');
      }

      // Wait for Stripe.js to load
      const StripeConstructor = await waitForStripe();
      
      // Create payment intent through our Node.js backend
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/payments/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(total * 100), // Convert to cents
          currency: 'usd', // Use USD as requested
          storeId: storeInfo.id,
          customerInfo: {
            name: customerName || 'Customer',
            email: customerEmail || '',
            phone: '',
            address: '',
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create payment intent');
      }

      const { clientSecret, paymentIntentId } = await response.json();

      if (!clientSecret) {
        throw new Error('Failed to initialize payment. Please try again.');
      }

      // Store complete order data for later use including all necessary info for order creation
      const orderData = {
        cart: cart,
        total: total,
        orderTotal: orderTotal,
        shippingTotal: shippingTotal,
        vatAmount: vatAmount,
        customerName,
        customerEmail,
        storeInfo: storeInfo,
        paymentIntentId: paymentIntentId
      };
      
      // Store cart items separately for reliable access using utility function
      storeCartItems(cart, total, orderTotal);
      localStorage.setItem('pendingStripeOrder', JSON.stringify(orderData));

      // Navigate to Stripe payment page with client secret
      navigate(`/stripe-payment?client_secret=${clientSecret}`);

      toast.dismiss(loadingToast);

    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Stripe payment error:', error);
      
      let errorMessage = 'Payment processing failed. ';
      if (error instanceof Error) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again or contact support if the issue persists.';
      }
      
      toast.error(errorMessage);
    }
  };

  const handlePayment = () => {
    if (paymentMethod === 'stripe') {
      handleStripePayment();
    } else {
      onConfirm();
    }
  };

  const getButtonLabel = () => {
    if (paymentMethod === 'bank') {
      return 'Confirm Payment';
    }
    return `Pay ${formatPrice(total)}`;
  };

  const renderAmountDetails = () => (
    <div className="mb-4 p-4 bg-gradient-to-br from-brand-50 to-brand-100/50 rounded-xl border border-brand-200">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 bg-brand-500/20 rounded-full flex items-center justify-center">
          <svg className="w-3 h-3 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="font-semibold text-gray-900 text-sm">Order Summary</h3>
      </div>
      <div className="space-y-3">
        {hasDiscounts && (
          <>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Original Subtotal:</span>
              <span className="text-sm font-medium text-gray-500 line-through">
                {formatPrice(cartTotals.originalSubtotal)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-brand-600 font-medium">Bulk Discounts:</span>
              <span className="text-sm font-medium text-brand-600">
                -{formatPrice(cartTotals.totalDiscount)}
              </span>
            </div>
          </>
        )}
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">
            {hasDiscounts ? 'Discounted Subtotal:' : 'Subtotal:'}
          </span>
          <span className="text-sm font-medium text-gray-900">{formatPrice(orderTotal)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">Shipping:</span>
          <span className="text-sm font-medium text-gray-900">{formatPrice(shippingTotal)}</span>
        </div>
        {vatAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">TAX:</span>
            <span className="text-sm font-medium text-gray-900">{formatPrice(vatAmount)}</span>
          </div>
        )}
        <div className="border-t border-brand-200 pt-2 mt-2">
          <div className="flex justify-between">
            <span className="text-base font-bold text-gray-900">Total:</span>
            <span className="text-base font-bold text-brand-600">{formatPrice(total)}</span>
          </div>
          {hasDiscounts && (
            <div className="mt-2 p-2 bg-brand-50 rounded-lg border border-brand-200">
              <div className="text-center">
                <span className="text-sm font-medium text-brand-700">
                  🎉 You saved {formatPrice(cartTotals.totalDiscount)} with bulk discounts!
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <Dialog.Panel className="mx-auto w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl bg-white shadow-2xl border-t sm:border border-brand-200 flex flex-col max-h-[95vh] sm:max-h-[90vh]">
          <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-brand-200 bg-gradient-to-r from-brand-50 to-brand-100/80 flex-shrink-0">
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-brand-100 rounded-full flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                Select Payment Method
              </Dialog.Title>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            {renderAmountDetails()}
            
            <div className="space-y-4">
              {storeInfo?.mb_active === 1 && (
                <div className={`p-4 border-2 rounded-xl transition-all duration-200 cursor-pointer hover:shadow-md ${
                  paymentMethod === 'bank' 
                    ? 'border-brand-500 bg-brand-50 shadow-sm' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      value="bank"
                      checked={paymentMethod === 'bank'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'bank')}
                      className="mt-1 mr-4 w-4 h-4 text-brand-600 focus:ring-brand-500 focus:ring-2"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="font-semibold text-gray-900">Bank Transfer</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Transfer directly to our bank account</p>
                      {paymentMethod === 'bank' && (
                        <div className="mt-3 space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-600">
                            Bank: <span className="font-bold">{storeInfo.m_bank}</span>
                          </p>

                          <div className="flex items-center space-x-2">
                            <p className="text-sm text-gray-600">
                              Account: <span className="font-bold select-text">{storeInfo.m_ac_num}</span>
                            </p>

                            <button
                              onClick={() => handleCopyAccountNumber(storeInfo.m_ac_num)}
                              aria-label="Copy account number"
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                            >
                              <Copy className="h-4 w-4 text-gray-500" />
                            </button>
                          </div>

                          <p className="text-sm text-gray-600">
                            Account Name: <span className="font-bold">{storeInfo.m_ac_name}</span>
                          </p>

                          {showSeparateShipping ? (
                            <>
                              <p className="text-sm text-brand-600 font-medium pt-1">
                                Goods Amount: <b>{formatPrice(orderTotal + vatAmount)}</b>
                              </p>
                              
                              <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                              
                                <p className="text-sm text-gray-600">
                                  Shipping Payment Details:
                                </p>
                                <div className="flex items-center space-x-2">
                                  <p className="text-sm text-gray-600">
                                    Account: <span className="font-bold select-text">{storeInfo.sepShippingAcn}</span>
                                  </p>
                                  <button
                                    onClick={() => handleCopyAccountNumber(storeInfo.sepShippingAcn)}
                                    aria-label="Copy shipping account"
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                  >
                                    <Copy className="h-4 w-4 text-gray-500" />
                                  </button>
                                </div>
                                  <p className="text-sm text-brand-600 font-medium">
                                  Shipping Amount: <b>{formatPrice(shippingTotal)}</b>
                                </p>
                                <p className="text-sm text-purple-600 mt-2">
                                  Note: If shipping payment is combined with order payment, it will not be refunded and the order will not be shipped.
                                </p>
                              </div>
                            </>
                          ) : (
                            <p className="text-sm text-brand-600 font-medium pt-1">
                              Payable Amount: <b>{formatPrice(total)}</b>
                            </p>
                          )}
                          
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Upload Payment Receipt{showSeparateShipping ? ' (Order)' : ''}
                            </label>
                            <div className="flex items-center justify-center w-full">
                              <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                orderPaymentReceipt 
                                  ? 'border-brand-500 bg-brand-50' 
                                  : 'border-brand-200 bg-brand-50/30 hover:border-brand-300 hover:bg-brand-50/50'
                              }`}>
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                  <Upload className={`w-8 h-8 mb-4 ${
                                    orderPaymentReceipt ? 'text-brand-600' : 'text-gray-500'
                                  }`} />
                                  {orderPaymentReceipt ? (
                                    <p className="text-sm text-brand-600 font-medium">
                                      {orderPaymentReceipt.name}
                                    </p>
                                  ) : (
                                    <>
                                      <p className="mb-2 text-sm text-gray-500">
                                        <span className="font-semibold">Click to upload</span> or drag and drop
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        PNG, JPG or PDF (MAX. 2MB)
                                      </p>
                                    </>
                                  )}
                                </div>
                                <input 
                                  type="file" 
                                  className="hidden" 
                                  accept="image/*,.pdf"
                                  onChange={(e) => onFileUpload(e, 'order')}
                                />
                              </label>
                            </div>

                            {showSeparateShipping && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Upload Shipping Payment Receipt
                                </label>
                                <div className="flex items-center justify-center w-full">
                                  <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                    shippingPaymentReceipt 
                                      ? 'border-brand-500 bg-brand-50' 
                                      : 'border-brand-200 bg-brand-50/30 hover:border-brand-300 hover:bg-brand-50/50'
                                  }`}>
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                      <Upload className={`w-8 h-8 mb-4 ${
                                        shippingPaymentReceipt ? 'text-brand-600' : 'text-gray-500'
                                      }`} />
                                      {shippingPaymentReceipt ? (
                                        <p className="text-sm text-brand-600 font-medium">
                                          {shippingPaymentReceipt.name}
                                        </p>
                                      ) : (
                                        <>
                                          <p className="mb-2 text-sm text-gray-500">
                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            PNG, JPG or PDF (MAX. 2MB)
                                          </p>
                                        </>
                                      )}
                                    </div>
                                    <input 
                                      type="file" 
                                      className="hidden" 
                                      accept="image/*,.pdf"
                                      onChange={(e) => onFileUpload(e, 'shipping')}
                                    />
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              )}

              {storeInfo?.pays_active === 1 && (
                <div className={`p-4 border-2 rounded-xl transition-all duration-200 cursor-pointer hover:shadow-md ${
                  paymentMethod === 'paystack' 
                    ? 'border-brand-500 bg-brand-50 shadow-sm' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      value="paystack"
                      checked={paymentMethod === 'paystack'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'paystack')}
                      className="mt-1 mr-4 w-4 h-4 text-brand-600 focus:ring-brand-500 focus:ring-2"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <span className="font-semibold text-gray-900">Pay with Paystack</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Secure card payments powered by Paystack
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {storeInfo?.monify_active === 1 && (
                <div className={`p-4 border-2 rounded-xl transition-all duration-200 cursor-pointer hover:shadow-md ${
                  paymentMethod === 'monnify' 
                    ? 'border-brand-500 bg-brand-50 shadow-sm' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      value="monnify"
                      checked={paymentMethod === 'monnify'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'monnify')}
                      className="mt-1 mr-4 w-4 h-4 text-brand-600 focus:ring-brand-500 focus:ring-2"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-semibold text-gray-900">Pay with Monnify</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Secure payments via Monnify gateway
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {(storeInfo?.StripeKey && storeInfo?.StripeSecrete) && (
                <div className={`p-4 border-2 rounded-xl transition-all duration-200 cursor-pointer hover:shadow-md ${
                  paymentMethod === 'stripe'
                    ? 'border-brand-500 bg-brand-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'stripe')}
                      className="mr-3 w-4 h-4 text-brand-600 focus:ring-brand-500 focus:ring-2"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">Pay with Stripe</span>
                      <p className="text-sm text-gray-500 mt-1">
                        Credit/debit cards, Apple Pay, Google Pay
                      </p>
                      {paymentMethod === 'stripe' && !storeInfo?.StripeKey && (
                        <p className="text-sm text-red-500 mt-2 p-2 bg-red-50 rounded border border-red-200">
                          Stripe payments are currently unavailable. Please choose another payment method or contact the store administrator.
                        </p>
                      )}
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Split payment instructions were moved into the bank payment section so they scroll on small screens. */}

          <div className="p-4 sm:p-6 border-t border-brand-100 bg-brand-50/30 flex-shrink-0">
            <div className="flex justify-end space-x-2 sm:space-x-3">
              <button
                onClick={onClose}
                className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base text-gray-700 bg-white hover:bg-brand-50 border border-brand-200 rounded-xl transition-all duration-200 font-medium hover:shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                disabled={(paymentMethod === 'stripe' && !storeInfo?.StripeKey) || isLoading}
                className="px-5 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:shadow-none flex items-center gap-2 min-w-[120px] sm:min-w-[140px]"
              >
                {isLoading ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                )}
                {isLoading ? 'Processing...' : getButtonLabel()}
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}


